import React, { useState } from 'react';
import { Search, Menu, X } from 'lucide-react';
import { useFamilyStore } from '../../store/familyStore';
import { currentRoute, pushRoute } from '../../utils/routing';

interface HeaderProps {
  onMenuToggle: () => void;
  familyName: string;
}

export const Header: React.FC<HeaderProps> = ({ onMenuToggle, familyName }) => {
  const { searchQuery, setSearchQuery } = useFamilyStore();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const currentPath = currentRoute();

  const navLinks = [
    { label: 'Family Tree', path: '/family-tree/Munshi-family' },
    { label: 'About', path: '/about' },
    { label: 'Contact', path: '/contact' },
    { label: 'Gallery', path: '/gallery' },
  ];

  const isActive = (path: string) => currentPath === path;

  const navigate = (path: string) => {
    pushRoute(path);
    setMobileNavOpen(false);
  };

  return (
    <header className="bg-white border-b border-stone-200 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">

          {/* Left: Menu toggle + Logo */}
          <div className="flex items-center space-x-4">
            <button
              onClick={onMenuToggle}
              className="lg:hidden p-2 rounded-md hover:bg-stone-100"
            >
              <Menu className="w-5 h-5 text-stone-600" />
            </button>

            <button
              onClick={() => navigate('/')}
              className="flex items-center space-x-2 hover:opacity-80 transition-opacity"
            >
              <img src="/logo.svg" alt="Munshis logo" className="h-8 w-auto" />
              <span className="font-serif text-xl font-medium text-stone-800">Munshis</span>
            </button>
          </div>

          {/* Center: Nav Links (desktop) */}
          <nav className="hidden lg:flex items-center space-x-8">
            {navLinks.map((link) => (
              <button
                key={link.path}
                onClick={() => navigate(link.path)}
                className={`text-sm font-medium transition-colors ${
                  isActive(link.path)
                    ? 'text-green-700'
                    : 'text-stone-600 hover:text-green-700'
                }`}
              >
                {link.label}
              </button>
            ))}
          </nav>

          {/* Right: Search + Mobile nav toggle */}
          <div className="flex items-center space-x-3">
            <div className="relative hidden md:block">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-stone-400 pointer-events-none" />
              <input
                type="text"
                placeholder="Search members..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 pr-4 py-2 w-48 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-600 focus:border-transparent"
              />
            </div>

            {/* Mobile nav toggle */}
            <button
              onClick={() => setMobileNavOpen(!mobileNavOpen)}
              className="lg:hidden p-2 text-stone-500 hover:text-stone-700 rounded-lg hover:bg-stone-100 transition-colors"
            >
              {mobileNavOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Nav */}
      {mobileNavOpen && (
        <div className="lg:hidden border-t border-stone-100 bg-white">
          <div className="px-4 py-3 space-y-2">
            {navLinks.map((link) => (
              <button
                key={link.path}
                onClick={() => navigate(link.path)}
                className={`block w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isActive(link.path)
                    ? 'bg-green-50 text-green-700'
                    : 'text-stone-700 hover:bg-stone-50'
                }`}
              >
                {link.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </header>
  );
};
